import React, { useRef, useEffect } from "react";
import type { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

const ReadOnlyContext = React.createContext<boolean>(false);

interface EditableTextProps {
  value: string;
  onUpdate: (newValue: string) => void;
  style?: React.CSSProperties;
  className?: string;
  multiline?: boolean;
}

const EditableText = ({
  value,
  onUpdate,
  style,
  className,
  multiline = false,
}: EditableTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const readOnly = React.useContext(ReadOnlyContext);

  useEffect(() => {
    if (textRef.current && textRef.current.innerText !== value) {
      textRef.current.innerText = value;
    }
  }, [value]);

  const handleBlur = () => {
    if (textRef.current) {
      const newValue = textRef.current.innerText.trim();
      if (newValue !== value) {
        onUpdate(newValue);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      textRef.current?.blur();
    }
  };

  if (readOnly) {
    return (
      <div
        style={{
          outline: "none",
          ...style,
        }}
        className={className?.replace(/hover:bg-blue-50\/50|focus:bg-blue-100\/50/g, '')}
      >
        {value}
      </div>
    );
  }

  return (
    <div
      ref={textRef}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        outline: "none",
        cursor: "text",
        transition: "background-color 0.2s",
        ...style,
      }}
      className={className || `hover:bg-blue-50/50 focus:bg-blue-100/50 transition-colors rounded-sm px-1 -mx-1`}
    >
      {value}
    </div>
  );
};

interface CVTemplateProps {
  data: CVData;
  onUpdate?: (newData: CVData) => void;
  readOnly?: boolean;
}

const SectionHeading = ({ children, onUpdate }: { children: string; onUpdate?: (val: string) => void }) => {
  const readOnly = React.useContext(ReadOnlyContext);
  return (
    <div
      className="section-header-box"
      style={{
        backgroundColor: "#000",
        color: "#ffffff",
        fontWeight: 700,
        fontSize: "11px",
        letterSpacing: "1.2px",
        textTransform: "uppercase",
        padding: "0 12px 9px",
        display: "inline-block",
        height: "24px",
        lineHeight: "22px",
        marginBottom: "12px",
        fontFamily: "Arial, sans-serif",
        WebkitPrintColorAdjust: "exact",
      }}
    >
      {onUpdate && !readOnly ? (
        <EditableText
          value={children}
          onUpdate={onUpdate}
          style={{ 
            backgroundColor: "transparent",
            color: "#ffffff",
            lineHeight: "22px",
            display: "inline-block",
            verticalAlign: "top",
          }}
        />
      ) : (
        <span style={{ color: "#ffffff", lineHeight: "22px", display: "inline-block", verticalAlign: "top" }}>{children}</span>
      )}
    </div>
  );
};

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const CVTemplate = React.forwardRef<HTMLDivElement, CVTemplateProps>(
  ({ data, onUpdate, readOnly = false }, ref) => {
    
    const debouncedUpdate = React.useCallback(
      debounce((newData: CVData) => {
        if (onUpdate) onUpdate(newData);
      }, 300),
      [onUpdate]
    );

    const updateHeader = (key: "name" | "jobTitle", val: string) => {
      debouncedUpdate({ ...data, [key]: val });
    };

    const updateSectionName = (key: keyof NonNullable<CVData["sectionNames"]>, val: string) => {
      debouncedUpdate({ 
        ...data, 
        sectionNames: { ...(data.sectionNames || {}), [key]: val } as any 
      });
    };

    const updateBasicInfo = (index: number, val: string) => {
      const newInfo = [...data.basicInfo];
      newInfo[index] = val;
      debouncedUpdate({ ...data, basicInfo: newInfo });
    };

    const updateProfile = (val: string) => {
      debouncedUpdate({ ...data, profile: val });
    };

    const updateSkill = (index: number, val: string) => {
      const newSkills = [...data.keySkills];
      newSkills[index] = val;
      debouncedUpdate({ ...data, keySkills: newSkills });
    };

    const updateWorkEntry = (index: number, field: keyof WorkEntry, val: any) => {
      const newExperience = [...data.workExperience];
      newExperience[index] = { ...newExperience[index], [field]: val };
      debouncedUpdate({ ...data, workExperience: newExperience });
    };

    const updateWorkBullet = (entryIndex: number, bulletIndex: number, val: string) => {
      const newExperience = [...data.workExperience];
      const newBullets = [...newExperience[entryIndex].bullets];
      newBullets[bulletIndex] = val;
      newExperience[entryIndex] = { ...newExperience[entryIndex], bullets: newBullets };
      debouncedUpdate({ ...data, workExperience: newExperience });
    };

    const updateEducationEntry = (index: number, field: keyof EducationEntry, val: any) => {
      const newEducation = [...data.education];
      newEducation[index] = { ...newEducation[index], [field]: val };
      debouncedUpdate({ ...data, education: newEducation });
    };

    const updateEducationNote = (entryIndex: number, noteIndex: number, val: string) => {
      const newEducation = [...data.education];
      const newNotes = [...newEducation[entryIndex].notes];
      newNotes[noteIndex] = val;
      newEducation[entryIndex] = { ...newEducation[entryIndex], notes: newNotes };
      debouncedUpdate({ ...data, education: newEducation });
    };

    const updateInterest = (index: number, val: string) => {
      const newInterests = [...data.interests];
      newInterests[index] = val;
      debouncedUpdate({ ...data, interests: newInterests });
    };

    const updateReferences = (val: string) => {
      debouncedUpdate({ ...data, references: val });
    };

    const sNames = data.sectionNames || {
      basicInfo: "Basic Information",
      profile: "Profile",
      keySkills: "Key Skills",
      workExperience: "Work Experience",
      education: "Education",
      interests: "Interests",
      references: "References"
    };

    return (
      <ReadOnlyContext.Provider value={readOnly}>
        <div
          ref={ref}
          id="cv-template-root"
          style={{
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: "'Helvetica', 'Arial', sans-serif",
            padding: "48px 52px",
            width: "794px",
            minHeight: "1123px",
            boxSizing: "border-box",
            margin: "0 auto",
            position: "relative",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
          }}
        >
        {/* ── HEADER ── */}
        <div data-pdf-id="cv-name-container" style={{ marginBottom: "0px" }}>
          <EditableText
            value={data.name.toUpperCase()}
            onUpdate={(val) => updateHeader("name", val)}
            style={{
              fontSize: "45px",
              fontWeight: 700,
              color: "#000",
              letterSpacing: "-1px",
              lineHeight: 1,
              display: "block",
            }}
          />
        </div>

        <div data-pdf-id="cv-title-container" style={{ marginBottom: "8px" }}>
          <EditableText
            value={data.jobTitle}
            onUpdate={(val) => updateHeader("jobTitle", val)}
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#000",
              display: "block",
            }}
          />
        </div>

        <div
          className="header-underline"
          style={{
            height: "3px",
            backgroundColor: "#000",
            marginBottom: "24px",
          }}
        />

        {/* ── BASIC INFORMATION ── */}
        <div style={{ marginBottom: "20px" }}>
          <SectionHeading onUpdate={(val) => updateSectionName("basicInfo", val)}>
            {sNames.basicInfo}
          </SectionHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {data.basicInfo.map((line, i) => (
              <EditableText
                key={i}
                value={line}
                onUpdate={(val) => updateBasicInfo(i, val)}
                style={{ fontSize: "13px", color: "#000", lineHeight: "1.2", fontWeight: 400 }}
              />
            ))}
          </div>
        </div>

        {/* ── PROFILE ── */}
        <div style={{ marginBottom: "20px" }}>
          <SectionHeading onUpdate={(val) => updateSectionName("profile", val)}>
            {sNames.profile}
          </SectionHeading>
          <EditableText
            value={data.profile}
            onUpdate={updateProfile}
            multiline
            style={{
              fontSize: "13px",
              color: "#000",
              lineHeight: "1.7",
              textAlign: "left",
              fontWeight: 400,
            }}
          />
        </div>

        {/* ── KEY SKILLS ── */}
        <div style={{ marginBottom: "20px" }}>
          <SectionHeading onUpdate={(val) => updateSectionName("keySkills", val)}>
            {sNames.keySkills}
          </SectionHeading>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px" }}>
            {data.keySkills.map((skill, i) => (
              <div key={i} style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                <span style={{ fontSize: "13px", color: "#000" }}>•</span>
                <EditableText
                  value={skill}
                  onUpdate={(val) => updateSkill(i, val)}
                  style={{ fontSize: "13px", color: "#000", flex: 1, fontWeight: 400 }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── WORK EXPERIENCE ── */}
        <div style={{ marginBottom: "20px" }}>
          <SectionHeading onUpdate={(val) => updateSectionName("workExperience", val)}>
            {sNames.workExperience}
          </SectionHeading>
          {data.workExperience.map((entry, i) => (
            <div key={i} style={{ marginBottom: "16px" }}>
              <div
                className="experience-header-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  width: "100%",
                }}
              >
                <EditableText
                  value={entry.jobTitle}
                  onUpdate={(val) => updateWorkEntry(i, "jobTitle", val)}
                  style={{ fontWeight: 700, fontSize: "14px", color: "#000", flex: 1 }}
                />
                <EditableText
                  value={entry.dates}
                  onUpdate={(val) => updateWorkEntry(i, "dates", val)}
                  style={{
                    fontSize: "13px",
                    color: "#000",
                    whiteSpace: "nowrap",
                    marginLeft: "auto",
                    textAlign: "right",
                    fontWeight: 400,
                    minWidth: "115px",
                  }}
                />
              </div>
              <div style={{ marginTop: "1px" }}>
                <EditableText
                  value={entry.company}
                  onUpdate={(val) => updateWorkEntry(i, "company", val)}
                  style={{ fontSize: "13px", color: "#000", display: "block", fontWeight: 400 }}
                />
              </div>
              <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "2px" }}>
                {entry.bullets.map((b, bi) => (
                  <div key={bi} style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                    <span style={{ fontSize: "13px", color: "#000" }}>•</span>
                    <EditableText
                      value={b}
                      onUpdate={(val) => updateWorkBullet(i, bi, val)}
                      multiline
                      style={{ fontSize: "13px", color: "#000", lineHeight: "1.6", flex: 1, fontWeight: 400 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── EDUCATION ── */}
        <div style={{ marginBottom: "20px" }}>
          <SectionHeading onUpdate={(val) => updateSectionName("education", val)}>
            {sNames.education}
          </SectionHeading>
          {data.education.map((entry, i) => (
            <div key={i} style={{ marginBottom: "16px" }}>
              <div
                className="experience-header-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  width: "100%",
                }}
              >
                <EditableText
                  value={entry.qualification}
                  onUpdate={(val) => updateEducationEntry(i, "qualification", val)}
                  style={{ fontWeight: 700, fontSize: "14px", color: "#000", flex: 1 }}
                />
                <EditableText
                  value={entry.dates}
                  onUpdate={(val) => updateEducationEntry(i, "dates", val)}
                  style={{
                    fontSize: "13px",
                    color: "#000",
                    whiteSpace: "nowrap",
                    marginLeft: "auto",
                    textAlign: "right",
                    fontWeight: 400,
                    minWidth: "115px",
                  }}
                />
              </div>
              <div style={{ marginTop: "1px" }}>
                <EditableText
                  value={entry.institution}
                  onUpdate={(val) => updateEducationEntry(i, "institution", val)}
                  style={{ fontSize: "13px", color: "#000", display: "block", fontWeight: 400 }}
                />
              </div>
              {entry.notes.map((n, ni) => (
                <EditableText
                  key={ni}
                  value={n}
                  onUpdate={(val) => updateEducationNote(i, ni, val)}
                  style={{ fontSize: "13px", color: "#000", marginTop: "1px", fontWeight: 400 }}
                />
              ))}
            </div>
          ))}
        </div>

        {/* ── INTERESTS ── */}
        {data.interests.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("interests", val)}>
              {sNames.interests}
            </SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {data.interests.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                  <span style={{ fontSize: "13px", color: "#000" }}>•</span>
                  <EditableText
                    value={item}
                    onUpdate={(val) => updateInterest(i, val)}
                    style={{ fontSize: "13px", color: "#000", flex: 1, fontWeight: 400 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── REFERENCES ── */}
        {data.references && (
          <div style={{ marginBottom: "20px" }}>
            <SectionHeading onUpdate={(val) => updateSectionName("references", val)}>
              {sNames.references}
            </SectionHeading>
            <EditableText
              value={data.references}
              onUpdate={updateReferences}
              style={{ fontSize: "13px", color: "#000", display: "block", fontWeight: 400 }}
            />
          </div>
        )}
      </div>
    </ReadOnlyContext.Provider>
  );
}
);

CVTemplate.displayName = "CVTemplate";
(CVTemplate as any).templateName = "Classic";

export default CVTemplate;
